import React, { useEffect, useState, useRef, useCallback } from "react";
import mixpanel from "mixpanel-browser";
import axios from "axios";
import logo from "../sidebar-logo.png";
import Player, { PlayerWrapper } from "../Components/PlayerNew";
import { observer } from "mobx-react";
import ShareModal from '../Components/ShareModal/index';
import { Share as ShareIcon } from '@mui/icons-material';
import { Link, useHistory, useLocation } from "react-router-dom";
import "./Share.scss";
import FeedSubscribed from "../Pages/DiscoverNew/Components/FeedSubscribed";
import { CircularProgress } from "@mui/material";
import { getAllFeeds, getLatestFeedArticleFromFeed } from "../audiblogsApi";
import OneGiftLogo from '../Components/OneGiftLogo';
import { Helmet } from 'react-helmet';
import { setLandingPage } from '../utils/trackingParams';

const onPreviousArticle = () => null;
const onNextArticle = () => null;
const onArticleEnded = () => null;

const FEEDS_PER_PAGE = 10;

const SharePlayer = observer(
  ({
    match: {
      params: { filename },
    },
  }) => {
    const [isFullPlayer, updateIsFullPlayer] = useState(false);
    const [currentArticle, setCurrentArticle] = useState(null);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [allFeeds, setAllFeeds] = useState([]);
    const [displayedFeeds, setDisplayedFeeds] = useState([]);
    const [feedsLoading, setFeedsLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(0);
    const [showTranscript, setShowTranscript] = useState(false);
    const observer = useRef();
    const loadingRef = useRef();
    const history = useHistory();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const currentParams = searchParams.toString();

    // Add effect to preserve URL parameters
    useEffect(() => {
      // If we have params but they're not in the URL, update the URL
      if (currentParams && !location.search) {
        history.replace({
          pathname: location.pathname,
          search: currentParams
        });
      }
    }, [location, history, currentParams]);

    // Add changeCurrentArticle function
    const changeCurrentArticle = (article) => {
      setCurrentArticle(article);
    };

    const loadMoreFeeds = useCallback(() => {
      if (!feedsLoading && hasMore) {
        const start = page * FEEDS_PER_PAGE;
        const end = start + FEEDS_PER_PAGE;
        const newFeeds = allFeeds.slice(start, end);
        
        if (newFeeds.length > 0) {
          setDisplayedFeeds(prev => [...prev, ...newFeeds]);
          setPage(prev => prev + 1);
          setHasMore(end < allFeeds.length);
        } else {
          setHasMore(false);
        }
      }
    }, [page, allFeeds, feedsLoading, hasMore]);

    // Intersection observer setup
    useEffect(() => {
      if (loadingRef.current && hasMore) {
        observer.current = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting && hasMore) {
            loadMoreFeeds();
          }
        }, { threshold: 0.5 });

        observer.current.observe(loadingRef.current);
      }

      return () => {
        if (observer.current) {
          observer.current.disconnect();
        }
      };
    }, [loadMoreFeeds, hasMore]);

    useEffect(() => {
      // Track page visit with consistent event name
      mixpanel.track('Landing Page Visited', {
        page: 'share',
        filename: filename
      });
      setLandingPage('share');

      console.log("Fetching article with filename:", filename);
      axios
        .get(
          `${process.env.REACT_APP_BACKEND_URL}/api/article/${encodeURIComponent(filename)}`
        )
        .then(({ data }) => {
          console.log("Article data received:", data);
          // Format the article data for the player
          const formattedArticle = {
            ...data,
            article_id: data.article_id.replace(/^https?:\/\//, ''), // Remove http/https prefix
            text: data.text || data.content || data.article_text // Get text from any of these fields
          };
          // console.log("Article text content:", formattedArticle.text);
          setCurrentArticle(formattedArticle);
        })
        .catch(error => {
          console.error("Error fetching article:", error);
          console.error("Error response:", error.response?.data);
        });

      // Fetch all feeds
      getAllFeeds()
        .then(async (response) => {
          if (response && response.data && response.data.feeds) {
            // Process feeds to add thumbnail from last_article_url
            const processedFeeds = response.data.feeds.map(feed => {
              return {
                ...feed,
                feed_id: feed.feed_id || feed.id || `feed-${Math.random()}`,
                title: feed.title || feed.name || "Untitled Feed",
                date_added: feed.date_added || feed.created_at || new Date().toISOString(),
                article_id: feed.last_article_url || feed.feed_id || feed.id,
                audio_url: feed.audio_url || "https://audioread.com/share/placeholder.mp3",
              };
            });
            
            // Fetch latest article thumbnails for all feeds
            const feedsWithThumbnails = await Promise.all(
              processedFeeds.map(async (feed) => {
                try {
                  if (feed.feed_id) {
                    const latestArticle = await getLatestFeedArticleFromFeed(feed.feed_id);
                    
                    if (latestArticle) {
                      return {
                        ...feed,
                        thumbnail_url: latestArticle.thumbnail_url || feed.thumbnail_url,
                        audio_url: latestArticle.audio_url,
                        article_id: latestArticle.article_id,
                        article_title: latestArticle.title,
                        article_url: latestArticle.url,
                        latestArticle: latestArticle
                      };
                    }
                  }
                  return feed;
                } catch (error) {
                  console.error(`Error fetching latest article for feed ${feed.feed_id}:`, error);
                  return feed;
                }
              })
            );
            
            // Only take first 10 feeds
            setDisplayedFeeds(feedsWithThumbnails.slice(0, 10));
          }
          setFeedsLoading(false);
        })
        .catch(error => {
          console.error("Error fetching feeds:", error);
          setFeedsLoading(false);
        });
    }, [filename]);

    // Redirect to signin when trying to subscribe
    const handleSubscribe = () => {
      history.push('/signin');
    };

    return (
      <>
        {currentArticle && (
          <Helmet>
            <title>{currentArticle?.title ? `${currentArticle.title} - Listen to this article | Audioread` : 'Audioread'}</title>
            <meta name="description" content={`Listen to "${currentArticle?.title || 'this article'}" as a podcast. Convert any article to audio with Audioread. Perfect for listening on the go.`} />
            <meta name="keywords" content={`${currentArticle?.title || ''}, podcast, audio article, text to speech, listen to articles, audiobooks, Audioread`} />
            
            <meta property="og:title" content={`${currentArticle?.title || 'Article'} - Listen as a podcast`} />
            <meta property="og:description" content={`Listen to "${currentArticle?.title || 'this article'}" as a podcast. Convert any article to audio with Audioread.`} />
            <meta property="og:type" content="article" />
            <meta property="og:url" content={window.location.href} />
            <meta property="og:image" content={currentArticle?.thumbnail_url || "https://audioread.com/logo.png"} />
            
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={`${currentArticle?.title || 'Article'} - Listen as a podcast`} />
            <meta name="twitter:description" content={`Listen to "${currentArticle?.title || 'this article'}" as a podcast. Convert any article to audio with Audioread.`} />
            <meta name="twitter:image" content={currentArticle?.thumbnail_url || "https://audioread.com/logo.png"} />
            
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "AudioObject",
                "name": currentArticle?.title,
                "description": `Listen to "${currentArticle?.title || 'this article'}" as a podcast. Convert any article to audio with Audioread.`,
                "contentUrl": currentArticle?.audio_url,
                "thumbnailUrl": currentArticle?.thumbnail_url || "https://audioread.com/logo.png",
                "publisher": {
                  "@type": "Organization",
                  "name": "Audioread",
                  "url": "https://audioread.com"
                },
                "url": window.location.href,
                "datePublished": currentArticle?.published_at || new Date().toISOString(),
                "duration": currentArticle?.duration || "PT10M"
              })}
            </script>
          </Helmet>
        )}
        
        <div className="nav-bar">
          <div style={{ display: "flex", alignItems: "flex-start" }}>
            <Link to={`/${currentParams ? `?${currentParams}` : ''}`} style={{ textDecoration: 'none' }}>
              <img src={logo} className="audiblogs-logo" alt="logo" />
            </Link>
            <div style={{ marginLeft: '50px' }} className="ph-badge">
              <a
                href="https://www.producthunt.com/posts/audiblogs?utm_source=badge-top-post-badge&utm_medium=badge&utm_souce=badge-audiblogs"
                target="_blank"
              >
                <img
                  src="https://api.producthunt.com/widgets/embed-image/v1/top-post-badge.svg?post_id=283759&theme=dark&period=monthly"
                  alt="Audioread - Listen to any web article in your podcast player | Product Hunt"
                  style={{
                    width: '250px',
                    height: '54px',
                  }}
                  width="250"
                  height="54"
                />
              </a>
            </div>
          </div>
          <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '20px', fontSize: '1.05rem', fontWeight: 'bold' }}>
              <Link
                className="signin-button"
                to={`/feeds${currentParams ? `?${currentParams}` : ''}`}
              >
                Feeds
              </Link>
              <Link
                className="signin-button"
                to={{ pathname: "https://help.audioread.com/en/collections/12375780-use-cases" }}
                target="_blank"
              >
                Use Cases
              </Link>
              <Link
                className="signin-button"
                to={`/signin?source=share_nav_signin${currentParams ? `&${currentParams}` : ''}`}
              >
                Sign In
              </Link>
              <div className="cta-div" style={{ position: 'static', width: 'auto', paddingBottom: '18px' }}>
                <Link
                  to={`/signin?source=share_nav_signup${currentParams ? `&${currentParams}` : ''}`}
                  className="cta-button"
                >
                  <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                    <div>
                      <div>Sign Up</div>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <PlayerWrapper showPlayer={true}>
          <main className="App-header" style={{
            minHeight: "calc(100vh - 86px)",
            display: "flex",
            flexDirection: "column",
            textAlign: "center",
            paddingTop: "80px"
          }}>
            <article>
              <h1
                id="headline"
                className="headline"
                style={{
                  margin: "50px auto",
                  maxWidth: "90%",
                  wordWrap: "break-word",
                  textAlign: "center",
                  fontSize: "2.5em",
                  color: "#fff"
                }}
              >
                {currentArticle ? currentArticle.title : "Loading article..."}
              </h1>
              <div
                style={{
                  marginBottom: "50px",
                  color: "lightgrey",
                }}
              >
                <a
                  href={currentArticle ? `//${currentArticle.article_id}` : ""}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "lightgrey" }}
                >
                  View Original ({currentArticle ? currentArticle.article_id : ""})
                </a>
                <div style={{ marginTop: "20px", display: "flex", justifyContent: "center", gap: "10px" }}>
                  <button 
                    className="share-button"
                    onClick={() => setIsShareModalOpen(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '4px',
                      background: '#f0f0f0',
                      color: '#333',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <ShareIcon />
                    <span>Share</span>
                  </button>
                  <button 
                    className="share-button"
                    onClick={() => setShowTranscript(!showTranscript)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '4px',
                      background: '#f0f0f0',
                      color: '#333',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <span>{showTranscript ? 'Hide Transcript' : 'Show Transcript'}</span>
                  </button>
                </div>
                {showTranscript && (
                  <div style={{
                    marginTop: '20px',
                    padding: '20px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    maxWidth: '800px',
                    margin: '20px auto',
                    color: '#fff',
                    whiteSpace: 'pre-wrap',
                    textAlign: 'left',
                    lineHeight: '1.6'
                  }}>
                    {currentArticle?.text ? currentArticle.text : "No transcript available"}
                  </div>
                )}
              </div>
              <p style={{ color: "rgb(216, 125, 176)", fontSize: "1.3em" }}>
                Listen to any article, PDF, email, and subscribe to your favorite feeds in your podcast player using{" "}
                <a
                  style={{ color: "rgb(216, 125, 176)" }}
                  href="https://audioread.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Audioread
                </a>{" "}
              </p>
            </article>

            {/* All Feeds Section */}
            <section className="article-container" style={{ 
              marginTop: "50px", 
              marginBottom: "50px",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: "20px",
              padding: "0 20px",
              width: "100%",
              maxWidth: "1200px",
              margin: "50px auto",
              justifyItems: "center",
              justifyContent: "center"
            }}>
              <header className="section-header" style={{ 
                marginTop: "0px", 
                marginBottom: "30px",
                gridColumn: "1 / -1"
              }}>
                <div className="divider-line"></div>
                <h2>All Feeds</h2>
                <div className="divider-line"></div>
              </header>
              {feedsLoading ? (
                <div className="feeds-loading-container" style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  padding: '100px 20px',
                  gap: '20px',
                  gridColumn: "1 / -1"
                }}>
                  <CircularProgress 
                    size={40} 
                    sx={{
                      color: '#4292fa',
                      '@keyframes gradient': {
                        '0%': { color: '#4292fa' },
                        '25%': { color: '#8535cd' },
                        '50%': { color: '#e82186' },
                        '75%': { color: '#ff5c57' },
                        '100%': { color: '#4292fa' }
                      },
                      animation: 'gradient 2s ease infinite'
                    }}
                  />
                  <p style={{ 
                    color: '#fff',
                    fontSize: '16px',
                    fontWeight: 500,
                    background: 'linear-gradient(90deg, #4292fa, #8535cd, #e82186, #ff5c57)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundSize: '300% 100%',
                    animation: 'gradient 2s ease infinite'
                  }}>Loading feeds...</p>
                </div>
              ) : (
                <>
                  {displayedFeeds.map((feed) => (
                    <Link 
                      key={feed.feed_id || feed.id || Math.random().toString()}
                      to={`/signin?source=share_feed_card${currentParams ? `&${currentParams}` : ''}`}
                      style={{
                        textDecoration: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease',
                        display: 'block'
                      }}
                      className="feed-link"
                    >
                      <FeedSubscribed
                        feed={feed}
                        idToken={null}
                        selectArticle={() => {}}
                        isActive={false}
                        onUnsubscribe={() => {}}
                        isSubscribed={false}
                      />
                    </Link>
                  ))}
                </>
              )}
            </section>
          </main>
        </PlayerWrapper>

        {/* Footer */}
        <div style={{ textAlign: 'center', paddingBottom: '20px', marginBottom: '100px' }}>
          <OneGiftLogo />
          <div className="footer-links" style={{ margin: '25px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
            <Link to={`/terms${currentParams ? `?${currentParams}` : ''}`}>Terms of Service</Link>
            <Link to={`/privacy${currentParams ? `?${currentParams}` : ''}`}>Privacy Policy</Link>
            <Link to={`/dmca${currentParams ? `?${currentParams}` : ''}`}>DMCA Policy</Link>
          </div>
          <div style={{ margin: '25px', opacity: 0.7 }}>© {new Date().getFullYear()} Audioread™. All rights reserved.</div>
        </div>

        <div className="share-player">
          <Player
            {...{
              isFullPlayer,
              updateIsFullPlayer,
              currentArticle,
              onPreviousArticle,
              onNextArticle,
              onArticleEnded,
              listeningWhere: "/share",
              autoPlay: false,
              fullPlayerLeaveSpace: 0,
            }}
          />
        </div>

        {currentArticle && (
          <ShareModal 
            isOpen={isShareModalOpen}
            onClose={() => setIsShareModalOpen(false)}
            article={currentArticle}
          />
        )}
      </>
    );
  }
);

export default SharePlayer;
